// utils/fridayDonationUtils.js
import { DateTime } from 'luxon';

export const calculateFridayDonations = ({
  startDate, // JS Date object from the DateSelector
  timeZone,
  selectedProducts,
  currency,
  splitOnDays = false,
  processingTime = '12:00'
}) => {
  /* ───────────────────────────────────────────────────────────
     1)  Total amount from selected products
  ─────────────────────────────────────────────────────────── */
  const totalExact = selectedProducts.reduce((sum, p) => {
    const price = p[currency.toLowerCase()] || 0;
    return sum + price * (p.quantity || 0);
  }, 0);
  const totalRounded = Math.round(totalExact);

  /* ───────────────────────────────────────────────────────────
     2)  Build a 4‑month window (start‑date → end‑of‑3rd‑next‑month)
  ─────────────────────────────────────────────────────────── */
  const startLuxon = DateTime.fromJSDate(startDate).setZone(timeZone).startOf('day');

  // 4 months total  •  e.g. 18 Apr →  end‑of‑Jul
  const finalEnd = startLuxon.plus({ months: 3 }).endOf('month');

  // All days in that window
  const allDates = [];
  for (let cursor = startLuxon; cursor <= finalEnd; cursor = cursor.plus({ days: 1 })) {
    allDates.push(cursor);
  }

  /* ───────────────────────────────────────────────────────────
     3)  Keep only Fridays
  ─────────────────────────────────────────────────────────── */
  const fridays = allDates.filter(d => d.weekday === 5);
  const numberOfFridays = fridays.length;

  if (numberOfFridays === 0) {
    // Nothing to pay
    return {
      donationSchedule: [],
      summary: {
        totalAmount: 0,
        totalShares: 0,
        shareValue: 0,
      },
    };
  }

  /* ───────────────────────────────────────────────────────────
     4)  Decide distribution logic
         A) splitOnDays → split total across all Fridays
         B) false       → pay total on every Friday
  ─────────────────────────────────────────────────────────── */
  const nowFull = DateTime.now().setZone(timeZone);
  const today = nowFull.startOf('day');
  const isAfter1130AM = nowFull.hour > 11 || (nowFull.hour === 11 && nowFull.minute >= 30);

  let schedule = [];
  let finalSum = 0;

  if (splitOnDays) {
    /* A) divide equally across Fridays */
    const shareValue = totalRounded / numberOfFridays;

    schedule = fridays.map(day => {
      // If Friday is “today” and already past 11 : 30, skip it
      const skip = day.hasSame(today, 'day') && isAfter1130AM;

      return {
        date: day.toISODate(),
        shares: skip ? 0 : 1,
        amount: skip ? 0 : Math.round(shareValue),
        isMultiplierNight: false,
      };
    });

    // fix ±1 rounding diff so overall sum == totalRounded
    let diff = totalRounded - schedule.reduce((s, d) => s + d.amount, 0);
    for (let i = 0; diff !== 0; i = (i + 1) % schedule.length) {
      if (schedule[i].shares === 1) {
        schedule[i].amount += diff > 0 ? 1 : -1;
        diff += diff > 0 ? -1 : 1;
      }
    }

    finalSum = totalRounded; // by construction
  } else {
    /* B) entire total on every payable Friday */
    schedule = fridays.map(day => {
      const skip = day.hasSame(today, 'day') && isAfter1130AM;

      return {
        date: day.toISODate(),
        shares: skip ? 0 : 1,
        amount: skip ? 0 : totalRounded,
        isMultiplierNight: false,
      };
    });

    finalSum = schedule.reduce((s, d) => s + d.amount, 0);
  }

  /* ───────────────────────────────────────────────────────────
     5)  (Optional) attach product breakdown & save to localStorage
         – unchanged from your original code –
  ─────────────────────────────────────────────────────────── */
  const handleAddPrd = () => {
    const tempSchedule = JSON.parse(JSON.stringify(schedule));
    let totalBase = 0;
    selectedProducts.forEach(p => (totalBase += p.quantity * (p[currency.toLowerCase()] || 0)));

    const withPct = selectedProducts.map(p => {
      const base = p.quantity * (p[currency.toLowerCase()] || 0);
      return { product: p, pct: totalBase ? (base * 100) / totalBase : 0 };
    });

    const scheduleWithProd = tempSchedule
      .filter(d => d.amount > 0)
      .map(d => {
        // allocate by percentage
        const alloc = withPct.map(item => {
          const raw = (d.amount * item.pct) / 100;
          return {
            product: { ...item.product },
            qty: Math.round(raw),
          };
        });

        // rounding correction
        let diff = d.amount - alloc.reduce((s, a) => s + a.qty, 0);
        for (let i = 0; diff !== 0; i = (i + 1) % alloc.length) {
          if (diff > 0) {
            alloc[i].qty++;
            diff--;
          } else if (diff < 0 && alloc[i].qty > 0) {
            alloc[i].qty--;
            diff++;
          }
        }

        alloc.forEach(a => (a.product.quantity = a.qty));
        return { ...d, products: alloc.map(a => a.product), currency, timeZone, processingTime };
      });
    localStorage.setItem('FRIDAY_DONATION', JSON.stringify(scheduleWithProd));
  };
  handleAddPrd();

  /* ───────────────────────────────────────────────────────────
     6)  Return schedule + summary
  ─────────────────────────────────────────────────────────── */
  return {
    donationSchedule: schedule,
    summary: {
      totalAmount: finalSum,
      totalShares: numberOfFridays,
      shareValue: splitOnDays ? totalRounded / numberOfFridays : totalRounded,
    },
  };
};
